/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react'
import App, { type AppContext, type AppProps } from 'next/app'
import { logVitals } from '@/components/Sitewide/webVitals'
import { RecoilRoot } from 'recoil'

// These were ambiguous-build-order global imports I am consolidating here
import '@csc/dls/dist/index.css'
// For the PLP page (including search, yes i know it's misnamed)
import '@/styles/category.css'
// base normalization of HTML and BODY nodes
import '@/components/Checkout/normalize.css' // RISKY ONE...
import '@/styles/extend.scss'
// These are global, though COULD be modules, and classnames non-intersecting.
import '@/components/Search/SearchNotFound.scss' // less risky now, not a module but also not global namespace

// These are global SCSS, just like the CSS ones above...
import '@/styles/header.scss'
import '@/styles/tailwind.scss'

// These are local files, with global imports - I am taking
//   a shot at resolving them to parent...
import '@/components/Pdp/AddProductToCartForm/style.scss'
import '@/components/Pdp/AccordionProductDetails/style.scss'

import dynamic from 'next/dynamic'
import Script from 'next/script'
import { ENABLE_EXTEND } from '@/services/Configuration'
import CSChat from '@/components/ScriptIntegrations/CSChat'
import CitizensPay from '../components/ScriptIntegrations/CitizensPay'

const Head = dynamic(import('next/head'))
const Listrak = dynamic(import('../components/ScriptIntegrations/Listrak'))
const FacebookScript = dynamic(import('../components/ScriptIntegrations/FacebookScript'))
const GoogleTagManager = dynamic(import('../components/ScriptIntegrations/GoogleTagManager'))
const GoogleMapsScript = dynamic(import('../components/ScriptIntegrations/GoogleMapsScript'))
const GoogleOptimizeScript = dynamic(import('../components/ScriptIntegrations/GoogleOptimizeScript'))
const UseRedirectLegacyIoSAppsWatchdog = dynamic(import('@/components/Sitewide/UseRedirectLegacyIoSAppsWatchdog'))
const ExtendWarranty = dynamic(import('@/components/ScriptIntegrations/ExtendWarranty'))
const WunderKind = dynamic(import('@/components/ScriptIntegrations/WunderKind'))
const ApplePayScript = dynamic(import('@/components/ScriptIntegrations/ApplePayScript'))
const VWOScript = dynamic(import('@/components/ScriptIntegrations/VWOScript'))
const Connexity = dynamic(import('@/components/ScriptIntegrations/ConnexityScript'))

type CustomAppProps = {
  returnAPIOnly: boolean,
}

const MyApp = ({ Component, pageProps }: AppProps<CustomAppProps>) => {
  useEffect(() => {
    logVitals()
  }, [])
  if (pageProps.returnAPIOnly) {
    return <Component {...pageProps} />
  }

  return (
    <RecoilRoot>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=no" />
      </Head>
      <CitizensPay />
      <GoogleOptimizeScript />
      <ApplePayScript />
      <GoogleMapsScript />
      <FacebookScript />
      <GoogleTagManager />
      <UseRedirectLegacyIoSAppsWatchdog />
      <CSChat />
      <WunderKind />
      <VWOScript />
      <Script strategy="worker" src="https://ajax.googleapis.com/ajax/libs/jquery/3.6.1/jquery.min.js" />
      <Listrak />
      <Connexity />
      { ENABLE_EXTEND && <ExtendWarranty /> }
      <Component {...pageProps} />
    </RecoilRoot>

  )
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const path = String(appContext.ctx.asPath || '')
  const pageProps = await App.getInitialProps(appContext)
  if (path.startsWith('/ssr-api')) {
    return {
      ...pageProps,
      returnAPIOnly: true,
    }
  }
  return {
    ...pageProps,
    returnAPIOnly: false,
  }
}

export default MyApp
